import {
    cacheKeys,
    useCurrentWorkspace,
    useCustomMutation,
    useCustomQuery,
} from '@gripp/shared-logic';
import {
    Colors,
    SaveFormTop,
    TopModalBar,
    useGetDimensions,
} from '@gripp/shared-ui';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import { Tab, TabList, TabPanel, Tabs } from 'react-tabs';
import { AssetRoutineSearchInput } from './assetRoutineSearchInput';
import { EditAssetRoutinesList } from './editAssetRoutinesList';

// GraphQL queries defined inline
const FIND_ASSET_ROUTINES = `
  query FindAssetRoutines($assetId: String!) {
    asset {
      findAssetRoutines(assetId: $assetId) {
        routines
      }
    }
  }
`;

const ASSIGN_ROUTINES_TO_ASSET = `
  mutation Asset($input: AssignToAssetInput!) {
    asset {
      assignToAsset(input: $input) {
        message
        success
      }
    }
  }
`;

type Props = {
  assetId: string;
  title: string;
  onComplete: () => void;
};

export const AssetRoutinesForm = ({
  assetId,
  title,
  onComplete,
}: Props) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { dimensions, onLayout } = useGetDimensions();
  const height = dimensions?.height ?? 0;
  const tableResultsBlockHeight = 50;
  const listMaxHeight = height - tableResultsBlockHeight;

  const [originalRoutineAssignments, setOriginalRoutineAssignments] = useState<
    string[] | undefined
  >();
  const [routineAssignments, setRoutineAssignments] = useState<
    string[] | undefined
  >(undefined);

  const [keyword, setKeyword] = useState('');
  const [tabIndex, setTabIndex] = useState(0);

  const { data: assignments, isPending: isAssignmentsLoading } = useCustomQuery(
    {
      query: FIND_ASSET_ROUTINES,
      cacheKey: [],
      variables: {
        assetId: assetId,
      },
    }
  );

  const { mutateAsync: assignRoutinesToAsset, isPending: isRoutinesLoading } =
    useCustomMutation({
      query: ASSIGN_ROUTINES_TO_ASSET,
    });

  const currentWorkspace = useCurrentWorkspace();

  useEffect(() => {
    if (assignments && !isAssignmentsLoading) {
      const routineAssignments =
        assignments.asset.findAssetRoutines.routines;
      setOriginalRoutineAssignments(routineAssignments);
      setRoutineAssignments(routineAssignments);
    }
  }, [
    assignments?.asset?.findAssetRoutines?.routines,
    isAssignmentsLoading,
  ]);

  const handleRoutinesSubmit = async () => {
    const input = {
      routineIds: routineAssignments,
      assetId,
      workspaceId: currentWorkspace.id,
    };

    await assignRoutinesToAsset(
      { input: input },
      {
        onSettled: async () => {
          await queryClient.invalidateQueries({
            queryKey: cacheKeys('asset').all,
          });
        },
      }
    );

    onComplete();
  };

  const isDirty = () => {
    return (
      originalRoutineAssignments &&
      routineAssignments &&
      !(
        originalRoutineAssignments.length === routineAssignments.length &&
        originalRoutineAssignments.every((item) =>
          routineAssignments.includes(item)
        ) &&
        routineAssignments.every((item) =>
          originalRoutineAssignments.includes(item)
        )
      )
    );
  };

  return (
    <SaveFormTop
      onSubmit={handleRoutinesSubmit}
      title={title}
      topBar={TopModalBar}
      onCancel={onComplete}
      isDirty={isDirty()}
    >
      <View style={styles.container}>
        <AssetRoutineSearchInput
          keyword={keyword}
          onChange={setKeyword}
          placeholder="assets.routineSearchPlaceholder"
        />

        <View style={[styles.assignmentContainer]} onLayout={onLayout}>
          <Tabs
            selectedIndex={tabIndex}
            onSelect={(index) => {
              setTabIndex(index);
            }}
          >
            <TabList>
              <Tab>{t('assets.routines')}</Tab>
            </TabList>

            <View style={styles.listContainer}>
              <TabPanel>
                {routineAssignments && currentWorkspace?.id && (
                  <EditAssetRoutinesList
                    workspaceId={currentWorkspace.id}
                    assetId={assetId}
                    maxHeight={listMaxHeight}
                    keyword={keyword}
                    assignments={routineAssignments}
                    updateAssignments={setRoutineAssignments}
                  />
                )}
              </TabPanel>
            </View>
          </Tabs>
        </View>
      </View>
    </SaveFormTop>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    height: '100%',
    paddingHorizontal: 20,
    paddingTop: 24,
    paddingBottom: 20,
  },
  assignmentContainer: {
    flex: 1,
    marginTop: 20,
  },
  listContainer: {
    borderTopRightRadius: 12,
    borderBottomRightRadius: 12,
    borderBottomLeftRadius: 12,
    overflow: 'hidden',
  },
  inputStyle: {
    backgroundColor: Colors.white,
    borderColor: Colors.grayDivider,
    borderWidth: 1,
    borderRadius: 8,
  },
}); 